import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FormInput } from '../components/FormControls.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) {
    return '—';
  }
  if (n < 1024) {
    return `${n} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function processKey(proc) {
  return String(proc?.pm_id ?? proc?.name ?? '');
}

const SORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'id', label: 'ID' },
  { key: 'status', label: 'Status' },
  { key: 'port', label: 'Port' },
  { key: 'cpu', label: 'CPU' },
  { key: 'memory', label: 'Memory' },
  { key: 'restarts', label: 'Restarts' },
];

function processPort(proc) {
  if (proc?.port != null && proc.port !== '') {
    return Number(proc.port);
  }
  if (Array.isArray(proc?.ports) && proc.ports.length) {
    return Number(proc.ports[0]);
  }
  return null;
}

function sortValue(proc, key) {
  switch (key) {
    case 'name':
      return proc.name ? String(proc.name).toLowerCase() : null;
    case 'id': {
      const n = Number(proc.pm_id);
      return Number.isFinite(n) ? n : null;
    }
    case 'status':
      return proc.status ? String(proc.status).toLowerCase() : null;
    case 'port': {
      const n = processPort(proc);
      return n != null && Number.isFinite(n) ? n : null;
    }
    case 'cpu': {
      const n = Number(proc.monit?.cpu);
      return Number.isFinite(n) ? n : null;
    }
    case 'memory': {
      const n = Number(proc.monit?.memory);
      return Number.isFinite(n) ? n : null;
    }
    case 'restarts': {
      const n = Number(proc.restart_time);
      return Number.isFinite(n) ? n : null;
    }
    default:
      return null;
  }
}

function compareSort(a, b, key, dir) {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  if (av == null && bv == null) {
    return 0;
  }
  if (av == null) {
    return 1;
  }
  if (bv == null) {
    return -1;
  }
  const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
  return dir === 'desc' ? -cmp : cmp;
}

function mergeLiveProcess(existing, live) {
  const prev = existing || {};
  return {
    ...prev,
    pm_id: live.pm_id ?? prev.pm_id,
    name: live.name ?? prev.name,
    status: live.status ?? prev.status,
    pid: live.pid ?? prev.pid,
    port: live.port ?? prev.port ?? null,
    ports: Array.isArray(live.ports) && live.ports.length ? live.ports : prev.ports,
    monit: {
      ...(prev.monit || {}),
      cpu: live.cpu ?? prev.monit?.cpu,
      memory: live.memory ?? prev.monit?.memory,
    },
  };
}

const WS_STATUS = {
  idle: { label: 'Offline', className: 'text-bg-secondary' },
  connecting: { label: 'Connecting…', className: 'text-bg-warning' },
  live: { label: 'Live', className: 'text-bg-success' },
  error: { label: 'Stream error', className: 'text-bg-danger' },
};

export function AdminPm2Page() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [processes, setProcesses] = useState([]);
  const [version, setVersion] = useState(null);
  const [system, setSystem] = useState(null);
  const [metricsTs, setMetricsTs] = useState(null);
  const [wsStatus, setWsStatus] = useState('idle');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [logsFor, setLogsFor] = useState(null);
  const [logsType, setLogsType] = useState('out');
  const [logsLines, setLogsLines] = useState(100);
  const [logsContent, setLogsContent] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsClearing, setLogsClearing] = useState(false);
  const [sort, setSort] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const stoppedRef = useRef(false);
  const backoffRef = useRef(1000);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) {
        return { key, dir: 'asc' };
      }
      if (prev.dir === 'asc') {
        return { key, dir: 'desc' };
      }
      return null;
    });
  };

  const sortedProcesses = useMemo(() => {
    if (!sort) {
      return processes;
    }
    return [...processes].sort((a, b) => compareSort(a, b, sort.key, sort.dir));
  }, [processes, sort]);

  const flash = useCallback((text, isError = false) => {
    if (isError) {
      setError(text);
      setMessage('');
    } else {
      setMessage(text);
      setError('');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, ver] = await Promise.all([
        apiJson('/api/admin/pm2/processes'),
        apiJson('/api/admin/pm2/version').catch(() => null),
      ]);
      setProcesses(Array.isArray(list?.data) ? list.data : []);
      setVersion(ver?.data ?? null);
    } catch (e) {
      flash(e.message, true);
      setProcesses([]);
    } finally {
      setLoading(false);
    }
  }, [flash]);

  const clearTimers = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const disconnectWs = useCallback(() => {
    clearTimers();
    if (wsRef.current) {
      try {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    }
  }, []);

  const connectWs = useCallback(async () => {
    if (stoppedRef.current) {
      return;
    }

    disconnectWs();
    setWsStatus('connecting');

    let credentials;
    try {
      credentials = await apiJson('/api/admin/pm2/ws-credentials');
    } catch (e) {
      setWsStatus('error');
      reconnectTimerRef.current = setTimeout(() => {
        void connectWs();
      }, backoffRef.current);
      backoffRef.current = Math.min(backoffRef.current * 2, 15000);
      return;
    }

    if (!credentials?.url || stoppedRef.current) {
      setWsStatus('error');
      return;
    }

    const ws = new WebSocket(credentials.url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (stoppedRef.current) {
        ws.close();
        return;
      }
      setWsStatus('live');
      backoffRef.current = 1000;

      const expiresAt = credentials.expires_at ? Date.parse(credentials.expires_at) : 0;
      if (expiresAt > Date.now()) {
        const refreshIn = Math.max(30_000, expiresAt - Date.now() - 15_000);
        refreshTimerRef.current = setTimeout(() => {
          void connectWs();
        }, refreshIn);
      }
    };

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(String(event.data));
      } catch {
        return;
      }

      if (msg?.type === 'hello') {
        return;
      }

      if (msg?.type === 'error') {
        setWsStatus('error');
        return;
      }

      if (msg?.type !== 'metrics') {
        return;
      }

      setMetricsTs(msg.ts || Date.now());
      if (msg.system) {
        setSystem(msg.system);
      }

      const liveList = Array.isArray(msg.processes) ? msg.processes : [];
      setProcesses((prev) => {
        const byKey = new Map();
        for (const proc of prev) {
          byKey.set(processKey(proc), proc);
        }
        return liveList.map((live) => mergeLiveProcess(byKey.get(processKey(live)), live));
      });
    };

    ws.onerror = () => {
      setWsStatus('error');
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (stoppedRef.current) {
        setWsStatus('idle');
        return;
      }
      setWsStatus('connecting');
      reconnectTimerRef.current = setTimeout(() => {
        void connectWs();
      }, backoffRef.current);
      backoffRef.current = Math.min(backoffRef.current * 2, 15000);
    };
  }, [disconnectWs]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    stoppedRef.current = false;
    void connectWs();
    return () => {
      stoppedRef.current = true;
      disconnectWs();
    };
  }, [connectWs, disconnectWs]);

  const runAction = async (id, action, body) => {
    setBusyId(`${id}:${action}`);
    setError('');
    try {
      await apiJson(`/api/admin/pm2/processes/${encodeURIComponent(id)}/${action}`, {
        method: 'POST',
        body: body || undefined,
      });
      flash(`${action} requested for ${id}`);
      await load();
    } catch (e) {
      flash(e.message, true);
    } finally {
      setBusyId('');
    }
  };

  const openLogs = async (proc) => {
    const id = String(proc.name || proc.pm_id);
    setLogsFor(id);
    setLogsLoading(true);
    setLogsContent('');
    try {
      const res = await apiJson(
        `/api/admin/pm2/processes/${encodeURIComponent(id)}/logs?type=${encodeURIComponent(logsType)}&lines=${logsLines}`,
      );
      const data = res?.data;
      setLogsContent(typeof data?.content === 'string' ? data.content : JSON.stringify(data ?? res, null, 2));
    } catch (e) {
      setLogsContent('');
      flash(e.message, true);
    } finally {
      setLogsLoading(false);
    }
  };

  const refreshLogs = async () => {
    if (!logsFor) {
      return;
    }
    setLogsLoading(true);
    try {
      const res = await apiJson(
        `/api/admin/pm2/processes/${encodeURIComponent(logsFor)}/logs?type=${encodeURIComponent(logsType)}&lines=${logsLines}`,
      );
      const data = res?.data;
      setLogsContent(typeof data?.content === 'string' ? data.content : JSON.stringify(data ?? res, null, 2));
    } catch (e) {
      flash(e.message, true);
    } finally {
      setLogsLoading(false);
    }
  };

  const clearLogs = async (id = logsFor) => {
    if (!id) {
      return;
    }
    if (!window.confirm(`Clear PM2 logs for “${id}”? This cannot be undone.`)) {
      return;
    }
    setLogsClearing(true);
    setError('');
    try {
      await apiJson('/api/admin/pm2/logs/flush', {
        method: 'POST',
        body: { id },
      });
      flash(`Logs cleared for ${id}`);
      setLogsContent('');
      if (logsFor === id) {
        await refreshLogs();
      }
    } catch (e) {
      flash(e.message, true);
    } finally {
      setLogsClearing(false);
    }
  };

  if (!caps.pm2) {
    return (
      <div className="page">
        <p className="err">PM2 is not included in your current plan.</p>
      </div>
    );
  }

  const stream = WS_STATUS[wsStatus] || WS_STATUS.idle;
  const mem = system?.memory;

  return (
    <div className="page">
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
        <div>
          <h1 style={{ marginBottom: '0.25rem' }}>PM2 processes</h1>
          <p className="muted small" style={{ margin: 0 }}>
            Manage services on your PM2 host.{' '}
            <Link to="/admin/pm2/apps">npm apps →</Link>
            {' · '}
            <Link to="/admin/integrations">Integration settings →</Link>
          </p>
          {version ? (
            <p className="muted small" style={{ marginTop: '0.35rem' }}>
              API {version.api || '—'} · PM2 {version.pm2 || '—'}
            </p>
          ) : null}
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className={`badge ${stream.className}`}>{stream.label}</span>
          <button type="button" className="btn btn-outline-secondary" onClick={() => void load()} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error ? <p className="err">{error}</p> : null}
      {message ? <p className="ok">{message}</p> : null}

      {system ? (
        <div className="pm2-system-metrics">
          <div>
            <span className="muted small">Host CPU</span>
            <strong>{system.cpu_percent != null ? `${system.cpu_percent}%` : '—'}</strong>
          </div>
          <div>
            <span className="muted small">Memory</span>
            <strong>
              {mem ? `${formatBytes(mem.used)} / ${formatBytes(mem.total)} (${mem.used_percent ?? '—'}%)` : '—'}
            </strong>
          </div>
          <div>
            <span className="muted small">Load</span>
            <strong>
              {Array.isArray(system.loadavg) ? system.loadavg.map((n) => Number(n).toFixed(2)).join(' · ') : '—'}
            </strong>
          </div>
          <div>
            <span className="muted small">Updated</span>
            <strong>{metricsTs ? new Date(metricsTs).toLocaleTimeString() : '—'}</strong>
          </div>
        </div>
      ) : null}

      <div className="table-wrap" style={{ marginTop: '1rem' }}>
        <table className="data-table">
          <thead>
            <tr>
              {SORT_COLUMNS.map((col) => {
                const active = sort?.key === col.key;
                const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
                return (
                  <th key={col.key} aria-sort={ariaSort}>
                    <button
                      type="button"
                      className={`pm2-sort-btn${active ? ' is-active' : ''}`}
                      onClick={() => toggleSort(col.key)}
                    >
                      {col.label}
                      <span className="pm2-sort-indicator" aria-hidden="true">
                        {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  </th>
                );
              })}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && processes.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted">
                  Loading…
                </td>
              </tr>
            ) : null}
            {!loading && processes.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted">
                  No processes returned. Check the API key in Integrations.
                </td>
              </tr>
            ) : null}
            {sortedProcesses.map((proc) => {
              const id = String(proc.name || proc.pm_id);
              const busy = busyId.startsWith(`${id}:`);
              const portLabel =
                proc.port != null
                  ? String(proc.port)
                  : Array.isArray(proc.ports) && proc.ports.length
                    ? String(proc.ports[0])
                    : '—';
              return (
                <tr key={processKey(proc)}>
                  <td>{proc.name || '—'}</td>
                  <td>{proc.pm_id ?? '—'}</td>
                  <td>
                    <span className={`badge ${proc.status === 'online' ? 'text-bg-success' : 'text-bg-secondary'}`}>
                      {proc.status || '—'}
                    </span>
                  </td>
                  <td title={Array.isArray(proc.ports) && proc.ports.length > 1 ? proc.ports.join(', ') : undefined}>
                    {portLabel}
                  </td>
                  <td>{proc.monit?.cpu != null ? `${proc.monit.cpu}%` : '—'}</td>
                  <td>{formatBytes(proc.monit?.memory)}</td>
                  <td>{proc.restart_time ?? '—'}</td>
                  <td>
                    <div className="row-actions" style={{ flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={busy}
                        onClick={() => void runAction(id, 'restart')}
                      >
                        Restart
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={busy}
                        onClick={() => void runAction(id, 'reload')}
                      >
                        Reload
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-warning"
                        disabled={busy}
                        onClick={() => void runAction(id, 'stop')}
                      >
                        Stop
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => void openLogs(proc)}>
                        Logs
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={busy || logsClearing}
                        onClick={() => void clearLogs(id)}
                      >
                        Clear logs
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {logsFor ? (
        <div className="card-panel" style={{ marginTop: '1.25rem' }}>
          <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Logs — {logsFor}</h2>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setLogsFor(null)}>
              Close
            </button>
          </div>
          <div className="pm2-logs-controls" style={{ marginTop: '0.75rem' }}>
            <label className="field" style={{ margin: 0 }}>
              <span className="small">Type</span>
              <SearchableSelect
                className="form-select form-select-sm pm2-logs-type"
                value={logsType}
                onChange={(e) => setLogsType(e.target.value)}
              >
                <option value="out">out</option>
                <option value="err">err</option>
                <option value="log">log</option>
                <option value="both">both</option>
              </SearchableSelect>
            </label>
            <label className="field" style={{ margin: 0 }}>
              <span className="small">Lines</span>
              <FormInput
                type="number"
                min={1}
                max={5000}
                value={logsLines}
                onChange={(e) => setLogsLines(Number(e.target.value) || 100)}
              />
            </label>
            <button
              type="button"
              className="btn btn-sm btn-primary pm2-logs-load"
              disabled={logsLoading}
              onClick={() => void refreshLogs()}
            >
              {logsLoading ? 'Loading…' : 'Load'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              disabled={logsLoading || logsClearing}
              onClick={() => void clearLogs(logsFor)}
            >
              {logsClearing ? 'Clearing…' : 'Clear logs'}
            </button>
          </div>
          <pre
            style={{
              marginTop: '0.75rem',
              maxHeight: '420px',
              overflow: 'auto',
              background: 'var(--bs-tertiary-bg, #f8f9fa)',
              padding: '0.75rem',
              borderRadius: '6px',
              fontSize: '0.8rem',
            }}
          >
            {logsLoading ? 'Loading…' : logsContent || 'No log content.'}
          </pre>
        </div>
      ) : null}
    </div>
  );
}
